import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { FormsModule } from '@angular/forms';
import _ from 'lodash';
import { BehaviorFrequency } from '../../../insights.enums';
import { BehaviorDetailModel } from '../../../insights.interface';
import { behaviorDefinition } from '../define-behavior/behaviorDefinition';
import { BIP_SUMMARY_DATA } from './bip-summary-example';
import { BehaviourListModel } from '../../../../../core/models/common.models';
import { behaviorGoals } from '../../../../manage-settings/manage-settings.interface';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Subject } from 'rxjs';

export interface BipSummarySection {
  stepNumber: number;
  title: string;
  content: BipSectionContent;
}

export interface BipSectionContent {
  items?: string[];
  fields?: { label: string; value: string | number }[];
  frequencyFields?: { label: string; value: string | number }[];
  badges?: string[];
  checkboxes?: { label: string; checked: boolean }[];
  questions?: { label: string; value: string | number }[];
  description?: string;
  otherDescription?: string[];
}

@Component({
  selector: 'app-bip-summary',
  standalone: true,
  imports: [CommonModule, NzCheckboxModule, FormsModule, AngularSvgIconModule],
  templateUrl: './bip-summary.component.html',
  styleUrl: './bip-summary.component.scss',
})
export class BipSummaryComponent implements OnChanges, OnDestroy {
  @Input() behaviorDetail: BehaviorDetailModel | null | undefined = null;
  @Input() behaviourIntensityList: BehaviourListModel[] = [];
  @Input() behaviorGoal: behaviorGoals | null = null;
  @Output() managePreviewEvent = new EventEmitter<number>();
  behvaiorSummaryData = BIP_SUMMARY_DATA;
  sections = this.behvaiorSummaryData;
  private readonly destroy$ = new Subject<void>();
  totalSteps = 11;
  pageTitles: { title: string; controls: string[]; tooltip?: string }[] = [
    {
      title: 'Category',
      controls: ['caseId', 'behaviorCategoryId'],
      tooltip: 'Select behavior category',
    },
    {
      title: 'Behavior Types',
      controls: ['caseBehaviorTypeIds', 'caseBehaviorTypeDescriptions'],
      tooltip: 'Choose behavior types and descriptions',
    },
    {
      title: 'Operational Definition',
      controls: ['operationalDefinition'],
      tooltip: 'Define operational behavior characteristics',
    },
    {
      title: 'Frequency',
      controls: [
        'behaviourFrequencyId',
        'behaviourFrequencyCount',
        'isDurationRequired',
        'duration',
      ],
      tooltip: 'Specify behavior frequency and duration',
    },
    {
      title: 'Intensity',
      controls: ['behaviourIntensityId'],
      tooltip: 'Indicate behavior intensity level',
    },
    {
      title: 'Environments',
      controls: ['caseBehaviorEnvironmentIds'],
      tooltip: 'Select relevant environments',
    },
    {
      title: 'Setting Events',
      controls: ['caseBehaviorSettingEventIds'],
      tooltip: 'Choose applicable setting events',
    },
    {
      title: 'Antecedents',
      controls: [
        'caseBehaviorAntecedentIds',
        'caseBehaviorAntecedentDescription',
      ],
      tooltip: 'Identify behavior antecedents',
    },
    {
      title: 'Consequences',
      controls: [
        'caseBehaviorConsequenceIds',
        'caseBehaviorConsequenceDescription',
      ],
      tooltip: 'Specify behavior consequences',
    },
    {
      title: 'Goals',
      controls: ['goals'],
      tooltip: 'Define behavior goals',
    },
    {
      title: 'Questions',
      controls: ['caseBehaviorCustomQuestionAnswer'],
      tooltip: 'Answer custom questions',
    },
  ];
  ngOnChanges() {
    this.setUpSummaryData();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  get behvaiorCategory() {
    return this.behaviorDetail?.behaviorCategoryName || '';
  }
  getBehaviorFrequencyLabel(name: string) {
    return BehaviorFrequency[name as keyof typeof BehaviorFrequency];
  }

  private getDurationDisplayValue(): string {
    const duration = this.behaviorDetail?.duration;
    if (duration !== null && duration !== undefined) {
      return `${duration} minutes`;
    }

    const frequencyId = this.behaviorDetail?.behaviourFrequencyId;
    if (frequencyId !== null && frequencyId !== undefined) {
      return 'Duration does not matter or is instantaneous';
    }

    return 'Nothing entered';
  }

  private getFrequencyCountDisplayValue(): string | number {
    return this.behaviorDetail?.behaviourFrequencyCount ?? 'Nothing entered';
  }

  managePreview(page: number) {
    this.managePreviewEvent.emit(page);
  }
  setUpSummaryData() {
    this.sections = this.behvaiorSummaryData;
    this.sections[0].content = {
      items: [this.behaviorDetail?.behaviorCategoryName || 'Nothing entered'],
    };
    this.sections[1].content = {
      checkboxes: _.uniq(
        (
          this.behaviorDetail?.caseBehaviorTypes.map(
            ele => ele.behaviorTypeName
          ) || []
        ).filter(name => !name.includes('Other form of'))
      ).map(name => ({
        label: name,
        checked: true,
      })),
      otherDescription: _.uniq(
        this.behaviorDetail?.caseBehaviorTypeDescriptions.map(
          ele => ele.description
        ) || []
      )
        .map(desc => desc?.trim())
        .filter(Boolean),
    };
    const found = behaviorDefinition.find(
      item => item.id === this.behaviorDetail?.behaviorCategoryId
    );
    this.sections[2].content = {
      badges:
        this.behaviorDetail?.caseBehaviorTypes.map(
          type => type.behaviorTypeName
        ) || [],
      description:
        this.behaviorDetail?.operationalDefinition ||
        found?.definition ||
        'Nothing entered',
    };
    this.sections[3].content = {
      fields: [
        {
          label:
            'Which of the following best describes the frequency of the behavior?',
          value: this.behaviorDetail?.behaviourFrequencyName
            ? this.getBehaviorFrequencyLabel(
                this.behaviorDetail.behaviourFrequencyName
              )
            : 'Nothing entered',
        },
        {
          label: 'Once the behavior starts, how long does it usually last?',
          value: this.getDurationDisplayValue(),
        },
        {
          label: 'On average how many times a minute does the behavior occur?',
          value: this.getFrequencyCountDisplayValue(),
        },
      ],
    };
    const intensity = this.behaviourIntensityList.find(
      ele => ele.id === this.behaviorDetail?.behaviourIntensityId
    );
    this.sections[4].content = {
      fields: [
        {
          label: 'How intense is the behavior when it occurs?',
          value: intensity
            ? `${intensity?.name} (${intensity?.description})`
            : 'Nothing entered',
        },
      ],
    };
    this.sections[5].content = {
      checkboxes: _.uniqBy(
        (this.behaviorDetail?.caseBehaviorEnvironments || []).map(env => ({
          label: env.behaviorEnvironmentName,
          checked: true,
        })),
        'label'
      ),
    };
    this.sections[6].content = {
      checkboxes: _.uniqBy(
        (this.behaviorDetail?.caseBehaviorSettingEvents || []).map(event => ({
          label: event.behaviorSettingEventName,
          checked: true,
        })),
        'label'
      ),
    };
    this.sections[7].content = {
      checkboxes: _.uniqBy(
        (this.behaviorDetail?.caseBehaviorAntecedents || []).map(event => ({
          label: event.behaviorAntecedentName,
          checked: true,
        })),
        'label'
      ),
      otherDescription: _.uniq(
        this.behaviorDetail?.caseBehaviorAntecedentDescriptions.map(
          ele => ele.description
        ) || []
      )
        .map(desc => desc?.trim())
        .filter(Boolean),
    };
    this.sections[8].content = {
      checkboxes: _.uniqBy(
        (this.behaviorDetail?.caseBehaviorConsequences || []).map(event => ({
          label: event.behaviorConsequenceName,
          checked: true,
        })),
        'label'
      ),
      otherDescription: _.uniq(
        this.behaviorDetail?.caseBehaviorConsequenceDescriptions.map(
          ele => ele.description
        ) || []
      )
        .map(desc => desc?.trim())
        .filter(Boolean),
    };
    this.sections[9].content = {
      fields: [
        ...(this.behaviorGoal && this.behaviorGoal?.statusId !== 2
          ? [
              {
                label: 'District Instruction',
                value:
                  this.behaviorGoal?.templateInstructions || 'Nothing entered',
              },
            ]
          : []),
        {
          label: 'Goal',
          value: this.behaviorDetail?.goals || 'Nothing entered',
        },
      ],
    };
    this.sections[10].content = {
      questions:
        this.behaviorDetail?.caseBehaviorCustomQuestionAnswer.map(q => ({
          label: q.customQuestion,
          value: q.answer || 'Nothing entered',
        })) || [],
    };
  }
}
